export interface RankTier {
  id: string;
  title: string;
  emoji: string;
  minScore: number;
  color: string;       // tailwind gradient classes
  glowColor: string;  // rgba for box shadow
  description: string;
  perks: string[];
}

export interface BadgeDef {
  id: string;
  emoji: string;
  title: string;
  description: string;
  color: string;
}

export interface ScoreRules {
  taskCreated: number;
  promptPosted: number;
  taskCompleted: number;
  sprintBonus: number;
  promptStormBonus: number;
}

export const SCORE_RULES: ScoreRules = {
  taskCreated: 10,
  promptPosted: 5,
  taskCompleted: 15,
  sprintBonus: 20,
  promptStormBonus: 30,
};

export const RANK_TIERS: RankTier[] = [
  {
    id: 'seed',
    title: 'Seed',
    emoji: '🌱',
    minScore: 0,
    color: 'from-emerald-600/30 to-green-500/20',
    glowColor: 'rgba(52, 211, 153, 0.3)',
    description: 'Just planted in the network',
    perks: [
      'Access to task creation',
      'Browse the prompt board',
      'Connect with builders',
    ],
  },
  {
    id: 'builder',
    title: 'Builder',
    emoji: '⚡',
    minScore: 50,
    color: 'from-yellow-500/30 to-amber-400/20',
    glowColor: 'rgba(251, 191, 36, 0.35)',
    description: 'Creating with purpose',
    perks: [
      'All Seed perks',
      'Builder badge on profile',
      'Priority task visibility',
    ],
  },
  {
    id: 'connector',
    title: 'Connector',
    emoji: '🔗',
    minScore: 150,
    color: 'from-cyan-500/30 to-blue-400/20',
    glowColor: 'rgba(34, 211, 238, 0.35)',
    description: 'Weaving the knowledge network',
    perks: [
      'All Builder perks',
      'Connector profile ring',
      'Featured in community feed',
      'Access to collab tasks',
    ],
  },
  {
    id: 'architect',
    title: 'Architect',
    emoji: '🏗️',
    minScore: 350,
    color: 'from-blue-500/30 to-indigo-400/20',
    glowColor: 'rgba(99, 102, 241, 0.4)',
    description: 'Designing systems that matter',
    perks: [
      'All Connector perks',
      'Architect badge',
      'Advanced analytics access',
      'Task blueprint templates',
    ],
  },
  {
    id: 'innovator',
    title: 'Innovator',
    emoji: '💡',
    minScore: 700,
    color: 'from-violet-500/30 to-purple-400/20',
    glowColor: 'rgba(167, 139, 250, 0.4)',
    description: 'Driving real change through AI',
    perks: [
      'All Architect perks',
      'Innovator glow profile card',
      'Early access to new features',
      'Mentorship network access',
    ],
  },
  {
    id: 'pioneer',
    title: 'Pioneer',
    emoji: '🚀',
    minScore: 1400,
    color: 'from-pink-500/30 to-rose-400/20',
    glowColor: 'rgba(244, 114, 182, 0.4)',
    description: 'Leading the global frontier',
    perks: [
      'All Innovator perks',
      'Pioneer animated profile ring',
      'Highlighted in global leaderboard',
      'Private Pioneer community',
    ],
  },
  {
    id: 'luminary',
    title: 'Luminary',
    emoji: '✨',
    minScore: 2800,
    color: 'from-fuchsia-500/30 to-pink-400/20',
    glowColor: 'rgba(217, 70, 239, 0.45)',
    description: 'Illuminating paths for others',
    perks: [
      'All Pioneer perks',
      'Luminary spotlight feature',
      'Gold profile frame',
      'Cross-platform task syndication',
    ],
  },
  {
    id: 'visionary',
    title: 'Visionary',
    emoji: '🌐',
    minScore: 5000,
    color: 'from-amber-400/30 to-yellow-300/20',
    glowColor: 'rgba(251, 191, 36, 0.5)',
    description: 'Defining the next paradigm',
    perks: [
      'All Luminary perks',
      'Legendary gold animated halo',
      'Permanent featured placement',
      'Visionary council access',
      'Custom profile theme',
    ],
  },
];

export const BADGE_DEFS: BadgeDef[] = [
  {
    id: 'sprint_creator',
    emoji: '🔥',
    title: 'Sprint Creator',
    description: 'Created 5 tasks in a single week',
    color: 'from-orange-500/20 to-red-500/10 border-orange-400/30',
  },
  {
    id: 'prompt_storm',
    emoji: '⚡',
    title: 'Prompt Storm',
    description: 'Posted 20 prompts in a single week',
    color: 'from-yellow-500/20 to-amber-500/10 border-yellow-400/30',
  },
  {
    id: 'task_centurion',
    emoji: '🎯',
    title: 'Task Centurion',
    description: 'Created 100+ tasks lifetime',
    color: 'from-cyan-500/20 to-blue-500/10 border-cyan-400/30',
  },
  {
    id: 'task_master',
    emoji: '🏆',
    title: 'Task Master',
    description: 'Created 500+ tasks lifetime',
    color: 'from-amber-500/20 to-yellow-500/10 border-amber-400/30',
  },
  {
    id: 'voice_grid',
    emoji: '💬',
    title: 'Voice of the Grid',
    description: 'Posted 50+ prompts lifetime',
    color: 'from-violet-500/20 to-purple-500/10 border-violet-400/30',
  },
  {
    id: 'prompt_sage',
    emoji: '🧠',
    title: 'Prompt Sage',
    description: 'Posted 200+ prompts lifetime',
    color: 'from-indigo-500/20 to-blue-500/10 border-indigo-400/30',
  },
  {
    id: 'global_node',
    emoji: '🌍',
    title: 'Global Node',
    description: 'Profile completed + 10+ tasks created',
    color: 'from-green-500/20 to-emerald-500/10 border-green-400/30',
  },
  {
    id: 'ai_pioneer',
    emoji: '🤖',
    title: 'AI Pioneer',
    description: 'Earned 1000+ points from prompts alone',
    color: 'from-blue-500/20 to-cyan-500/10 border-blue-400/30',
  },
  {
    id: 'weekly_streak',
    emoji: '📅',
    title: 'Weekly Streak',
    description: 'Active for 4+ consecutive weeks',
    color: 'from-rose-500/20 to-pink-500/10 border-rose-400/30',
  },
];
